import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Home, Building2, Loader2, CheckCircle2, AlertCircle, ArrowRight } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { CONTACT } from "../../lib/site";
import { Field, SelectField } from "./FormFields";

const OPTIONS = {
  residencial: {
    instalacao: ["Painéis solares para autoconsumo", "Painéis solares com baterias (sob consulta)", "Ainda não sei — preciso de aconselhamento"],
  },
  empresarial: {
    instalacao: ["Autoconsumo em cobertura", "Autoconsumo em estrutura no solo", "Sistema com armazenamento (sob consulta)", "Ainda não sei — preciso de aconselhamento"],
  },
};
const HABITACAO = ["Moradia", "Apartamento / condomínio", "Outro"];
const TELHADO = ["Telha cerâmica", "Telhado plano / terraço", "Chapa / painel sandwich", "Não sei"];
const AREA = ["Menos de 100 m²", "100 a 500 m²", "500 a 1000 m²", "Mais de 1000 m²", "Não sei"];

const EMPTY = { nome: "", email: "", telefone: "", codigo_postal: "", localidade: "", tipo_instalacao: "", consumo: "", info: "", tipo_habitacao: "", tipo_telhado: "", empresa: "", atividade: "", area_disponivel: "", interesse_baterias: false, consentimento: false, website: "" };

const validate = (v, tipo) => {
  const e = {};
  if (v.nome.trim().length < 2) e.nome = "Indique o seu nome.";
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v.email.trim())) e.email = "Indique um e-mail válido.";
  if (!/^\+?[0-9 ]{9,16}$/.test(v.telefone.trim())) e.telefone = "Indique um telefone válido (9 dígitos).";
  if (!/^\d{4}-\d{3}$/.test(v.codigo_postal.trim())) e.codigo_postal = "Use o formato 0000-000.";
  if (v.localidade.trim().length < 2) e.localidade = "Indique a localidade.";
  if (!v.tipo_instalacao) e.tipo_instalacao = "Selecione o tipo de instalação.";
  if (tipo === "empresarial") {
    if (v.empresa.trim().length < 2) e.empresa = "Indique o nome da empresa.";
    if (v.atividade.trim().length < 2) e.atividade = "Indique a atividade da empresa.";
  }
  if (!v.consentimento) e.consentimento = "É necessário aceitar o tratamento dos dados para enviarmos a resposta.";
  return e;
};

const formatPostal = (raw) => {
  const d = raw.replace(/\D/g, "").slice(0, 7);
  return d.length > 4 ? `${d.slice(0, 4)}-${d.slice(4)}` : d;
};

export const QuoteForm = ({ defaultTipo = "residencial", origem = "website", lockTipo = false }) => {
  const [tipo, setTipo] = useState(defaultTipo);
  const [v, setV] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle");
  const [confirmed, setConfirmed] = useState(false);
  const [serverError, setServerError] = useState("");
  const started = useRef(Date.now());
  const formRef = useRef(null);

  const set = (k) => (ev) => {
    const val = ev.target.type === "checkbox" ? ev.target.checked : k === "codigo_postal" ? formatPostal(ev.target.value) : ev.target.value;
    setV((p) => ({ ...p, [k]: val }));
    if (errors[k]) setErrors((p) => ({ ...p, [k]: undefined }));
  };

  const switchTipo = (t) => {
    setTipo(t);
    setV((p) => ({ ...p, tipo_instalacao: "" }));
    setErrors({});
  };

  const submit = async (ev) => {
    ev.preventDefault();
    const e = validate(v, tipo);
    setErrors(e);
    if (Object.keys(e).length) {
      const first = formRef.current?.querySelector(`[name="${Object.keys(e)[0]}"]`);
      first?.focus();
      return;
    }
    setStatus("loading");
    setServerError("");
    try {
      const { data } = await api.post("/leads", { ...v, tipo_cliente: tipo, origem, elapsed_ms: Date.now() - started.current, consumo: v.consumo || null, info: v.info || null });
      setConfirmed(Boolean(data?.confirmacao_enviada));
      setStatus("success");
    } catch (err) {
      setServerError(formatApiError(err));
      setStatus("error");
    }
  };

  if (status === "success") {
    return (
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} role="status" aria-live="polite" data-testid="quote-success"
        className="rounded-[2rem] bg-forest p-8 text-white sm:p-12">
        <CheckCircle2 className="h-12 w-12 text-gold" strokeWidth={1.5} />
        <h3 className="mt-6 font-display text-3xl font-semibold tracking-tight sm:text-4xl">Pedido recebido. Obrigado, {v.nome.split(" ")[0]}.</h3>
        <p className="mt-4 max-w-lg text-white/80 leading-relaxed">O seu pedido de orçamento foi registado com sucesso. A equipa Mega Solar irá analisar as informações e entrar em contacto através do e-mail ou telefone indicados.</p>
        {confirmed && <p className="mt-3 max-w-lg text-white/80 leading-relaxed" data-testid="quote-confirmation-note">Enviámos uma confirmação para <strong className="text-white">{v.email}</strong>. Se não a encontrar, verifique a pasta de spam.</p>}
        <p className="mt-6 text-sm text-white/70">Se preferir, pode também contactar-nos diretamente: <a className="text-gold underline-offset-4 hover:underline" href={CONTACT.phoneHref}>{CONTACT.phoneDisplay}</a></p>
      </motion.div>
    );
  }

  const opts = OPTIONS[tipo];
  return (
    <form ref={formRef} onSubmit={submit} noValidate data-testid="quote-form" aria-label="Formulário de pedido de orçamento"
      className="relative rounded-[2rem] border border-forest/10 bg-white p-6 shadow-[0_40px_80px_-50px_rgba(10,46,32,0.45)] sm:p-10">
      {!lockTipo && (
        <fieldset>
          <legend className="text-sm font-medium text-ink">Tipo de cliente</legend>
          <div role="radiogroup" className="mt-3 grid grid-cols-2 gap-2 rounded-full bg-paper p-1.5">
            {[["residencial", "Residencial", Home], ["empresarial", "Empresarial", Building2]].map(([key, label, Icon]) => (
              <button key={key} type="button" role="radio" aria-checked={tipo === key} onClick={() => switchTipo(key)} data-testid={`quote-type-${key}`}
                className={`relative flex items-center justify-center gap-2 rounded-full py-3 text-sm font-medium transition-colors duration-300 ${tipo === key ? "text-white" : "text-ink/70 hover:text-forest"}`}>
                {tipo === key && <motion.span layoutId={`pill-${origem}`} className="absolute inset-0 rounded-full bg-forest" transition={{ type: "spring", stiffness: 400, damping: 34 }} />}
                <Icon className="relative h-4 w-4" /><span className="relative">{label}</span>
              </button>
            ))}
          </div>
        </fieldset>
      )}

      <div className="mt-8 grid gap-5 sm:grid-cols-2">
        <AnimatePresence initial={false}>
          {tipo === "empresarial" && (
            <motion.div key="biz" initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} className="grid gap-5 sm:col-span-2 sm:grid-cols-2">
              <Field name="empresa" label="Nome da empresa" value={v.empresa} onChange={set("empresa")} error={errors.empresa} required autoComplete="organization" />
              <Field name="atividade" label="Atividade da empresa" placeholder="Ex.: comércio, indústria, escritórios" value={v.atividade} onChange={set("atividade")} error={errors.atividade} required />
            </motion.div>
          )}
        </AnimatePresence>
        <Field name="nome" label={tipo === "empresarial" ? "Nome do responsável" : "Nome"} value={v.nome} onChange={set("nome")} error={errors.nome} required autoComplete="name" />
        <Field name="email" type="email" label="E-mail" value={v.email} onChange={set("email")} error={errors.email} required autoComplete="email" />
        <Field name="telefone" type="tel" label="Telefone" placeholder="9xx xxx xxx" value={v.telefone} onChange={set("telefone")} error={errors.telefone} required autoComplete="tel" />
        <div className="grid grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)] gap-3">
          <Field name="codigo_postal" label="Código postal" placeholder="0000-000" inputMode="numeric" value={v.codigo_postal} onChange={set("codigo_postal")} error={errors.codigo_postal} required autoComplete="postal-code" />
          <Field name="localidade" label="Localidade" value={v.localidade} onChange={set("localidade")} error={errors.localidade} required autoComplete="address-level2" />
        </div>
        <SelectField name="tipo_instalacao" label="Tipo de instalação pretendida" value={v.tipo_instalacao} onChange={set("tipo_instalacao")} options={opts.instalacao} error={errors.tipo_instalacao} required className="sm:col-span-2" />
        {tipo === "residencial" ? (
          <>
            <SelectField name="tipo_habitacao" label="Tipo de habitação" optional value={v.tipo_habitacao} onChange={set("tipo_habitacao")} options={HABITACAO} />
            <SelectField name="tipo_telhado" label="Tipo de telhado" optional value={v.tipo_telhado} onChange={set("tipo_telhado")} options={TELHADO} />
          </>
        ) : (
          <SelectField name="area_disponivel" label="Área disponível para instalação" optional value={v.area_disponivel} onChange={set("area_disponivel")} options={AREA} />
        )}
        <Field name="consumo" label={tipo === "empresarial" ? "Consumo mensal ou valor da fatura" : "Valor aproximado da fatura mensal"} optional placeholder={tipo === "empresarial" ? "Ex.: 2500 kWh ou 450 €/mês" : "Ex.: 90 €/mês"} value={v.consumo} onChange={set("consumo")} className={tipo === "empresarial" ? "" : "sm:col-span-2"} />
        <Field name="info" label="Informação adicional" optional textarea placeholder={tipo === "empresarial" ? "Horário de funcionamento, tipo de cobertura, objetivos…" : "Orientação do telhado, sombras, objetivos…"} value={v.info} onChange={set("info")} className="sm:col-span-2" />
      </div>

      <label className="mt-5 flex cursor-pointer items-start gap-3 text-sm text-ink/80">
        <input type="checkbox" name="interesse_baterias" checked={v.interesse_baterias} onChange={set("interesse_baterias")} data-testid="quote-batteries-checkbox" className="mt-0.5 h-5 w-5 shrink-0 rounded accent-[#0A2E20]" />
        Tenho interesse em analisar armazenamento com baterias (sujeito a disponibilidade).
      </label>

      <div className="absolute -left-[9999px]" aria-hidden="true">
        <label htmlFor={`website-${origem}`}>Website</label>
        <input id={`website-${origem}`} name="website" tabIndex={-1} autoComplete="off" value={v.website} onChange={set("website")} />
      </div>

      <div className="mt-6 rounded-2xl bg-paper p-5">
        <label className="flex cursor-pointer items-start gap-3 text-sm text-ink/85">
          <input type="checkbox" name="consentimento" checked={v.consentimento} onChange={set("consentimento")} aria-invalid={!!errors.consentimento} aria-describedby={errors.consentimento ? "err-consentimento" : undefined}
            data-testid="quote-consent-checkbox" className="mt-0.5 h-5 w-5 shrink-0 rounded accent-[#0A2E20]" />
          <span>Autorizo a Mega Solar a tratar os dados indicados exclusivamente para responder a este pedido de orçamento, nos termos da <Link to="/politica-de-privacidade" className="font-medium text-forest underline underline-offset-4">Política de Privacidade</Link>. *</span>
        </label>
        {errors.consentimento && <p id="err-consentimento" role="alert" className="mt-2 pl-8 text-sm text-red-700">{errors.consentimento}</p>}
      </div>

      {status === "error" && (
        <div role="alert" data-testid="quote-error" className="mt-6 flex gap-3 rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-800">
          <AlertCircle className="h-5 w-5 shrink-0" />
          <p>{serverError} O pedido não foi enviado. Pode tentar novamente ou contactar-nos pelo <a href={CONTACT.phoneHref} className="font-medium underline">{CONTACT.phoneDisplay}</a>.</p>
        </div>
      )}

      <div className="mt-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-xs text-ink/65">* Campos obrigatórios. Pedido de orçamento sem custos.</p>
        <button type="submit" disabled={status === "loading"} data-testid="quote-submit-button" className="btn-gold group disabled:opacity-70">
          {status === "loading" ? <><Loader2 className="h-4 w-4 animate-spin" /> A enviar…</> : <>Enviar pedido <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" /></>}
        </button>
      </div>
    </form>
  );
};
